import type { PointerEvent } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { Music2, SkipBack, SkipForward, X } from 'lucide-react'
import { PlayButton } from '@/components/player/PlayButton'
import { usePlayerActions, usePlayerProgress, usePlayerState } from '@/components/player/PlayerProvider'
import { EASE } from '@/components/dashboard/fx/motion'

const SOURCE_LABEL = { itunes: 'iTunes', deezer: 'Deezer' } as const

function formatTime(seconds: number) {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00'
  return `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`
}

// Docked at the bottom of every page (it lives above the router outlet), so a
// preview keeps playing as you click around.
export function PlayerBar() {
  const { track, status, source, kind, matched, queue } = usePlayerState()
  const { currentTime, duration } = usePlayerProgress()
  const { seek, close, next, prev } = usePlayerActions()

  const fraction = duration ? Math.min(currentTime / duration, 1) : 0

  const scrub = (event: PointerEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    seek((event.clientX - rect.left) / rect.width)
  }

  const position = queue ? `Track ${queue.index + 1} of ${queue.tracks.length} · ` : ''
  const note =
    status === 'unavailable'
      ? `${queue && queue.index < queue.tracks.length - 1 ? 'No preview for this one -- skipping ahead' : 'No preview found for this track'}`
      : kind === 'similar' && matched
        ? `${position}Previewing a different version: ${matched.artist} – ${matched.title}`
        : source
          ? `${position}${SOURCE_LABEL[source]} · 30-second preview`
          : `${position}Finding a preview…`

  return (
    <>
      {/* Keeps the last bit of every page from hiding behind the bar. */}
      {track && <div aria-hidden className="h-28" />}
      <div className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex justify-center px-4">
        <AnimatePresence>
          {track && (
            <motion.div
              key="player-bar"
              role="region"
              aria-label="Preview player"
              initial={{ y: 120, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 120, opacity: 0 }}
              transition={{ duration: 0.6, ease: EASE }}
              className="player-bar pointer-events-auto w-full max-w-2xl overflow-hidden rounded-2xl border border-border bg-card/80 shadow-2xl shadow-black/20 backdrop-blur-xl"
            >
              <div className="flex items-center gap-3 p-3 sm:gap-4 sm:p-4">
                {track.imageUrl ? (
                  <img src={track.imageUrl} alt="" className="size-12 shrink-0 rounded-lg object-cover ring-1 ring-border" />
                ) : (
                  <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                    <Music2 className="size-5" />
                  </div>
                )}

                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate font-medium">{track.title}</span>
                  <span className="truncate text-sm text-muted-foreground">{track.artist}</span>
                  <span className={`truncate text-xs ${kind === 'similar' || status === 'unavailable' ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground/80'}`}>
                    {note}
                  </span>
                </div>

                <span className="hidden font-mono text-xs tabular-nums text-muted-foreground sm:block">
                  {formatTime(currentTime)} / {formatTime(duration || 30)}
                </span>
                {queue && (
                  <button type="button" onClick={prev} aria-label="Previous track" className="hidden size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground sm:flex">
                    <SkipBack className="size-4 fill-current" />
                  </button>
                )}
                <PlayButton track={track} />
                {queue && (
                  <button
                    type="button"
                    onClick={next}
                    disabled={queue.index >= queue.tracks.length - 1}
                    aria-label="Next track"
                    className="flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-30"
                  >
                    <SkipForward className="size-4 fill-current" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={close}
                  aria-label="Close player"
                  className="flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <X className="size-4" />
                </button>
              </div>

              <div
                role="slider"
                aria-label="Seek"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(fraction * 100)}
                onPointerDown={scrub}
                className="group h-2 w-full cursor-pointer bg-muted"
              >
                <div className="h-full origin-left bg-gradient-to-r from-primary to-[#e87ba4]" style={{ width: `${fraction * 100}%` }} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  )
}
